
import React from 'react';
import { motion } from 'motion/react';
import { SimulationConfig, SpeedIncreaseMode, BallGrowthMode, ContainerShrinkMode } from '@/types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { SETTING_INFO } from '@/data/settingInfo';
import { CollapsibleSection } from './CollapsibleSection';
import { Toggle } from './Toggle';
import { Bolt, Clock, Activity, Zap } from 'lucide-react';

interface PhysicsSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const PhysicsSettingsSection: React.FC<PhysicsSettingsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "PRO", onUpgradeClick 
}) => {
    const speedModes: SpeedIncreaseMode[] = ['STATIC', 'TIME', 'COLLISION'];
    const growthModes: BallGrowthMode[] = ['NONE', 'TIME', 'COLLISION', 'PLAYLIST_SYNC'];
    const shrinkModes: ContainerShrinkMode[] = ['NONE', 'TIME', 'COLLISION'];

    const isRhythmic = config.mode === 'RHYTHMIC';
    const rhythmicMsg = "This engine is specifically designed for RHYTHMIC mode.";

    return (
      <div className="flex flex-col gap-0">
        <CollapsibleSection 
            title="Physics & Dynamics" 
            icon={Bolt} 
            darkMode={config.darkMode} 
            defaultOpen 
            settingKey="physicsDynamics"
            isLocked={isLocked}
            lockRequiredPlan={minPlan as any}
            onUpgradeClick={onUpgradeClick}
        >
            <div className="w-full flex flex-col gap-4 mb-4 mt-2">
                <SmartInput 
                    label="Gravity" 
                    min={0} max={0.5} step={0.001} 
                    value={config.gravity} 
                    onChange={v => setConfig(p => ({...p, gravity: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="gravity"
                />
                <SmartInput 
                    label="Bounce Power" 
                    min={0} max={2.0} step={0.01} 
                    value={config.restitution} 
                    onChange={v => setConfig(p => ({...p, restitution: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="restitution"
                />
                <SmartInput 
                    label="Bounce Randomization" 
                    min={0} max={1.0} step={0.01} 
                    value={config.bounceAngleRandomization || 0} 
                    onChange={v => setConfig(p => ({...p, bounceAngleRandomization: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="bounceAngleRandomization"
                />
                <SmartInput 
                    label="Ball Mass" 
                    min={0.1} max={10} step={0.1} 
                    value={config.ballMass} 
                    onChange={v => setConfig(p => ({...p, ballMass: v}))} 
                    darkMode={config.darkMode} 
                    settingKey="ballMass"
                />
            </div>

            <div className="w-full flex flex-col gap-4 mb-4">
                <ControlGroup label="Weight Growth" settingKey="ballWeightGrowthEnabled" darkMode={config.darkMode}>
                    <Toggle active={config.ballWeightGrowthEnabled} onClick={() => setConfig(p => ({...p, ballWeightGrowthEnabled: !p.ballWeightGrowthEnabled}))} />
                </ControlGroup>
                {config.ballWeightGrowthEnabled && (
                    <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className={`flex flex-col gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-cyan-500/5 border-cyan-500/10' : 'bg-white border-neutral-100 shadow-inner'}`}
                    >
                        <SmartInput label="Min Weight" min={0.1} max={100} step={0.1} value={config.ballMinWeight} onChange={v => setConfig(p => ({...p, ballMinWeight: v}))} darkMode={config.darkMode} settingKey="ballMinWeight" />
                        <SmartInput label="Max Weight" min={0.1} max={100} step={0.1} value={config.ballMaxWeight} onChange={v => setConfig(p => ({...p, ballMaxWeight: v}))} darkMode={config.darkMode} settingKey="ballMaxWeight" />
                        <SmartInput label="Growth Time (s)" min={1} max={300} step={1} value={config.ballWeightGrowthTime} onChange={v => setConfig(p => ({...p, ballWeightGrowthTime: v}))} darkMode={config.darkMode} settingKey="ballWeightGrowthTime" />
                    </motion.div>
                )}
            </div>
        </CollapsibleSection>


        {/* DYNAMICS: SPEED, GROWTH & CONTRACTION */}
        <CollapsibleSection 
            title="Dynamic Labs" 
            icon={Activity} 
            darkMode={config.darkMode}
            settingKey="dynamicsLab"
            isLocked={isLocked}
            lockRequiredPlan={minPlan as any}
            onUpgradeClick={onUpgradeClick}
        >
            <div className="w-full flex flex-col gap-6 mb-4 mt-2">
                
                {/* SPEED CONTROLS */}
                <div className="flex flex-col gap-4">
                    <span className="text-[11px] font-black uppercase text-cyan-500 opacity-80 mb-2 block tracking-widest">Global Speed Engine</span>
                    <ControlGroup label="Increase Mode" settingKey="speedIncreaseMode" darkMode={config.darkMode}>
                        <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-cyan-500/5 border-cyan-500/10' : 'bg-white border-neutral-100 shadow-sm'}`}>
                            <select 
                                value={config.speedIncreaseMode} 
                                onChange={e => setConfig(p => ({...p, speedIncreaseMode: e.target.value as SpeedIncreaseMode}))}
                                className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                            >
                                {speedModes.map(m => <option key={m} value={m} className={config.darkMode ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}>{m}</option>)}
                            </select>
                        </div>
                    </ControlGroup>
                    <div className={`grid grid-cols-1 gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}>
                        <SmartInput label="Initial Speed" min={0} max={5.0} step={0.05} value={config.initialSpeed} onChange={v => setConfig(p => ({...p, initialSpeed: v}))} darkMode={config.darkMode} settingKey="initialSpeed" />
                        <SmartInput label="Min Veloc. Limit" min={0} max={50} step={0.1} value={config.minVelocityLimit} onChange={v => setConfig(p => ({...p, minVelocityLimit: v}))} darkMode={config.darkMode} settingKey="minVelocityLimit" />
                        <SmartInput label="Max Veloc. Limit" min={0} max={200} step={1} value={config.maxVelocityLimit} onChange={v => setConfig(p => ({...p, maxVelocityLimit: v}))} darkMode={config.darkMode} settingKey="maxVelocityLimit" />
                        <SmartInput label="Increment Rate" min={0} max={0.1} step={0.0001} value={config.speedIncreaseRate} onChange={v => setConfig(p => ({...p, speedIncreaseRate: v}))} darkMode={config.darkMode} settingKey="speedIncreaseRate" />
                        <SmartInput label="Acceleration" min={0} max={0.01} step={0.00001} value={config.speedAcceleration} onChange={v => setConfig(p => ({...p, speedAcceleration: v}))} darkMode={config.darkMode} settingKey="speedAcceleration" />
                    </div>
                </div>

                {/* BALL GROWTH CONTROLS */}
                <div className="flex flex-col gap-4">
                    <span className="text-[11px] font-black uppercase text-cyan-400 opacity-80 mb-2 block tracking-widest">Expansion Dynamics</span>
                    <ControlGroup label="Growth Mode" settingKey="ballGrowthMode" darkMode={config.darkMode}>
                        <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-cyan-500/5 border-cyan-500/10' : 'bg-white border-neutral-100 shadow-sm'}`}>
                            <select 
                                value={config.ballGrowthMode} 
                                onChange={e => setConfig(p => ({...p, ballGrowthMode: e.target.value as BallGrowthMode}))}
                                className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                            >
                                {growthModes.map(m => <option key={m} value={m} className={config.darkMode ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}>{m}</option>)}
                            </select>
                        </div>
                    </ControlGroup>
                    
                    {config.ballGrowthMode !== 'NONE' && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className={`flex flex-col gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}
                        >
                            <SmartInput label="Growth Rate" min={0} max={2.0} step={0.001} value={config.ballGrowthRate || 0} onChange={v => setConfig(p => ({...p, ballGrowthRate: v}))} darkMode={config.darkMode} settingKey="ballGrowthRate" />
                            <SmartInput label="Growth Accel" min={0} max={0.1} step={0.0001} value={config.ballGrowthAcceleration || 0} onChange={v => setConfig(p => ({...p, ballGrowthAcceleration: v}))} darkMode={config.darkMode} settingKey="ballGrowthAcceleration" />
                            <div className="grid grid-cols-2 gap-4">
                                <SmartInput label="Min Radius" min={1} max={50} value={config.ballMinRadius} onChange={v => setConfig(p => ({...p, ballMinRadius: v}))} darkMode={config.darkMode} settingKey="ballMinRadius" />
                                <SmartInput label="Max Radius" min={5} max={1000} value={config.ballMaxRadius} onChange={v => setConfig(p => ({...p, ballMaxRadius: v}))} darkMode={config.darkMode} settingKey="ballMaxRadius" />
                            </div>
                        </motion.div>
                    )}
                </div>

                {/* CONTAINER SHRINK CONTROLS */}
                <div className="flex flex-col gap-4">
                    <span className="text-[11px] font-black uppercase text-cyan-500 opacity-80 mb-2 block tracking-widest">Container Contraction</span>
                    <ControlGroup label="Shrink Mode" settingKey="containerShrinkMode" darkMode={config.darkMode}>
                        <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-cyan-500/5 border-cyan-500/10' : 'bg-white border-neutral-100 shadow-sm'}`}>
                            <select 
                                value={config.containerShrinkMode} 
                                onChange={e => setConfig(p => ({...p, containerShrinkMode: e.target.value as ContainerShrinkMode}))}
                                className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                            >
                                {shrinkModes.map(m => <option key={m} value={m} className={config.darkMode ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}>{m}</option>)}
                            </select>
                        </div>
                    </ControlGroup>
                    
                    {config.containerShrinkMode !== 'NONE' && (
                        <motion.div 
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className={`flex flex-col gap-4 p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-inner'}`}
                        >
                            <SmartInput label="Shrink Rate" min={0} max={5.0} step={0.001} value={config.containerShrinkRate || 0} onChange={v => setConfig(p => ({...p, containerShrinkRate: v}))} darkMode={config.darkMode} settingKey="containerShrinkRate" />
                            <SmartInput label="Shrink Accel" min={0} max={0.1} step={0.0001} value={config.containerShrinkAcceleration || 0} onChange={v => setConfig(p => ({...p, containerShrinkAcceleration: v}))} darkMode={config.darkMode} settingKey="containerShrinkAcceleration" />
                            <SmartInput label="Min Radius" min={10} max={2000} value={config.minContainerRadius} onChange={v => setConfig(p => ({...p, minContainerRadius: v}))} darkMode={config.darkMode} settingKey="minContainerRadius" />
                        </motion.div>
                    )}
                </div>
            </div>
        </CollapsibleSection>
      </div>
    );
};
